import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Save,
  Image as ImageIcon,
  MessageSquare,
  Settings as SettingsIcon,
  Calendar,
  Users,
  Upload,
  Trash2,
  Plus,
  Eye,
  Mail,
  Phone,
  Edit3,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { api, formatError, resolveImage } from "@/lib/api";
import FinanceEditor from "@/components/admin/FinanceEditor";
import UserProfileMenu from "@/components/UserProfileMenu";

const Sidebar = ({ tab, setTab, onLogout }) => {
  const items = [
    { id: "branding", label: "Identidade Visual", icon: ImageIcon },
    { id: "hero", label: "Carrossel", icon: ImageIcon },
    { id: "cards", label: "Cards Rápidos", icon: SettingsIcon },
    { id: "doctor", label: "Sobre o Médico", icon: Users },
    { id: "services", label: "Serviços", icon: SettingsIcon },
    { id: "service_links", label: "Links de Serviços", icon: SettingsIcon },
    { id: "units", label: "Unidades", icon: SettingsIcon },
    { id: "schedule", label: "Horários", icon: Calendar },
    { id: "finance", label: "Financeiro", icon: SettingsIcon },
    { id: "testimonials", label: "Depoimentos", icon: MessageSquare },
    { id: "faqs", label: "FAQ", icon: MessageSquare },
    { id: "contact", label: "Contato/Redes", icon: Phone },
    { id: "messages", label: "Mensagens", icon: Mail },
  ];

  return (
    <aside className="w-64 bg-primary text-primary-foreground min-h-screen p-6 sticky top-0 hidden lg:block" data-testid="admin-sidebar">
      <div className="font-display text-xl mb-1">Dr. Alonso Neto</div>
      <div className="text-xs text-primary-foreground/50 uppercase tracking-widest mb-8">Painel Admin</div>
      <nav className="space-y-1">
        {items.map((i) => (
          <button
            key={i.id}
            onClick={() => setTab(i.id)}
            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition ${
              tab === i.id ? "bg-accent text-accent-foreground" : "hover:bg-primary-foreground/10 text-primary-foreground/80"
            }`}
            data-testid={`admin-tab-${i.id}`}
          >
            <i.icon className="w-4 h-4" /> {i.label}
          </button>
        ))}
        <a
          href="/admin/usuarios"
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition hover:bg-primary-foreground/10 text-primary-foreground/80 mt-8 mb-16"
          data-testid="admin-users-sidebar-link"
        >
          <Users className="w-4 h-4" /> Usuários
        </a>
      </nav>
      <button
        onClick={onLogout}
        className="absolute bottom-6 left-6 right-6 flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-primary-foreground/80 hover:bg-destructive hover:text-destructive-foreground transition"
        data-testid="admin-logout"
      >
        <LogOut className="w-4 h-4" /> Sair
      </button>
    </aside>
  );
};

const ImageUploader = ({ value, onChange, label, testid }) => {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Imagem enviada!");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Falha no upload");
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      {label && <label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</label>}
      <div className="flex items-center gap-3">
        <div className="w-24 h-24 bg-secondary/40 rounded-xl overflow-hidden flex items-center justify-center border border-border shrink-0">
          {value ? <img src={resolveImage(value)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-muted-foreground" />}
        </div>
        <div className="flex-1 space-y-2">
          <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL ou enviar imagem" className="w-full px-3 py-2 text-sm bg-secondary/30 rounded-lg outline-none focus:bg-white border border-transparent focus:border-primary" />
          <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml,image/x-icon,image/avif,image/bmp" ref={ref} onChange={upload} className="hidden" />
          <button type="button" onClick={() => ref.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90" data-testid={testid}>
            <Upload className="w-3 h-3" /> {uploading ? "Enviando..." : "Enviar imagem"}
          </button>
        </div>
      </div>
    </div>
  );
};

const TextField = ({ label, value, onChange, type = "text", textarea = false, rows = 3, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</label>
    {textarea ? (
      <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 px-3 py-2 text-sm bg-secondary/30 rounded-lg outline-none focus:bg-white border border-transparent focus:border-primary resize-none" data-testid={testid} />
    ) : (
      <input type={type} value={value || ""} onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} className="w-full mt-1 px-3 py-2 text-sm bg-secondary/30 rounded-lg outline-none focus:bg-white border border-transparent focus:border-primary" data-testid={testid} />
    )}
  </div>
);

const ListEditor = ({ items, onChange, fields, addLabel = "Adicionar", emptyTemplate, testid }) => {
  const [editing, setEditing] = useState({});

  const add = () => {
    const next = [...(items || []), { ...emptyTemplate }];
    onChange(next);
    setEditing((current) => ({ ...current, [next.length - 1]: true }));
  };

  const remove = (i) => {
    onChange((items || []).filter((_, k) => k !== i));
    setEditing((current) => {
      const next = {};
      Object.entries(current).forEach(([key, value]) => {
        const index = Number(key);
        if (index < i) next[index] = value;
        if (index > i) next[index - 1] = value;
      });
      return next;
    });
  };

  const update = (i, k, v) => onChange((items || []).map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const toggleEdit = (i) => setEditing((current) => ({ ...current, [i]: !current[i] }));

  return (
    <div className="space-y-4">
      {(items || []).map((it, i) => {
        const isEditing = Boolean(editing[i]);
        return (
          <div key={i} className="bg-secondary/20 p-5 rounded-2xl border border-border space-y-3" data-testid={`${testid}-item-${i}`}>
            <div className="flex flex-wrap justify-between items-center gap-3">
              <div className="text-xs uppercase tracking-widest text-primary font-medium">Item #{i + 1}</div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => toggleEdit(i)} className="text-primary hover:text-accent text-xs flex items-center gap-1 px-2 py-1 rounded-lg bg-white border border-border hover:border-accent transition" data-testid={`${testid}-edit-${i}`}>
                  <Edit3 className="w-3 h-3" /> {isEditing ? "Concluir" : "Editar"}
                </button>
                <button onClick={() => remove(i)} className="text-destructive hover:text-destructive/80 text-xs flex items-center gap-1 px-2 py-1 rounded-lg bg-white border border-border">
                  <Trash2 className="w-3 h-3" /> Remover
                </button>
              </div>
            </div>

            {isEditing ? (
              fields.map((f) =>
                f.type === "image" ? (
                  <ImageUploader key={f.key} label={f.label} value={it[f.key]} onChange={(v) => update(i, f.key, v)} testid={`${testid}-img-${i}`} />
                ) : (
                  <TextField key={f.key} label={f.label} value={it[f.key]} onChange={(v) => update(i, f.key, v)} type={f.type === "number" ? "number" : "text"} textarea={f.type === "textarea"} rows={f.rows} testid={`${testid}-${f.key}-${i}`} />
                )
              )
            ) : (
              <div className="grid md:grid-cols-2 gap-3">
                {fields.map((f) => (
                  <div key={f.key} className="bg-white/70 rounded-xl border border-border/60 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{f.label}</div>
                    {f.type === "image" && it[f.key] ? (
                      <img src={resolveImage(it[f.key])} alt="" className="h-16 w-16 rounded-lg object-cover border border-border" />
                    ) : (
                      <div className="text-sm text-primary font-medium whitespace-pre-wrap break-words">{it[f.key] || "Não informado"}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <button onClick={add} className="w-full py-3 rounded-xl border-2 border-dashed border-border text-sm text-muted-foreground hover:border-primary hover:text-primary flex items-center justify-center gap-2" data-testid={`${testid}-add`}>
        <Plus className="w-4 h-4" /> {addLabel}
      </button>
    </div>
  );
};

const Messages = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts");
      setItems(data);
    } catch (e) {
      toast.error("Erro ao carregar mensagens");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await api.patch(`/contacts/${id}`, { read: true });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta mensagem?")) return;
    await api.delete(`/contacts/${id}`);
    setSelected(null);
    load();
  };

  if (loading) return <div className="text-muted-foreground">Carregando mensagens…</div>;

  return (
    <div data-testid="admin-messages">
      <h2 className="font-display text-2xl text-primary mb-6">Mensagens Recebidas ({items.length})</h2>
      {items.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center text-muted-foreground">Nenhuma mensagem ainda.</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-2">
            {items.map((m) => (
              <button key={m.id} onClick={() => { setSelected(m); if (!m.read) markRead(m.id); }} className={`w-full text-left p-4 rounded-xl border transition ${selected?.id === m.id ? "border-accent bg-accent/5" : "border-border bg-white hover:border-primary"}`} data-testid={`message-${m.id}`}>
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-primary truncate">{m.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{m.subject || m.message.slice(0, 50)}</div>
                  </div>
                  {!m.read && <span className="bg-accent text-accent-foreground text-[10px] px-2 py-0.5 rounded-full">NOVO</span>}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">{new Date(m.created_at).toLocaleString("pt-BR")}</div>
              </button>
            ))}
          </div>
          <div className="bg-white rounded-2xl p-6 border border-border h-fit sticky top-6" data-testid="message-detail">
            {selected ? (
              <>
                <div className="flex justify-between items-start mb-5">
                  <div>
                    <div className="font-display text-xl text-primary">{selected.name}</div>
                    <div className="text-xs text-muted-foreground">{new Date(selected.created_at).toLocaleString("pt-BR")}</div>
                  </div>
                  <button onClick={() => remove(selected.id)} className="text-destructive hover:opacity-80 text-sm flex items-center gap-1"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="space-y-3 text-sm">
                  <div><span className="text-muted-foreground">E-mail:</span> <a className="text-primary" href={`mailto:${selected.email}`}>{selected.email}</a></div>
                  {selected.phone && <div><span className="text-muted-foreground">Telefone:</span> {selected.phone}</div>}
                  {selected.subject && <div><span className="text-muted-foreground">Assunto:</span> {selected.subject}</div>}
                  <div className="pt-3 border-t border-border whitespace-pre-wrap text-foreground/85 leading-relaxed">{selected.message}</div>
                </div>
              </>
            ) : (
              <div className="text-center text-muted-foreground py-10">Selecione uma mensagem para ver detalhes</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const { settings, refresh, setSettings } = useSettings();
  const navigate = useNavigate();
  const [tab, setTab] = useState("branding");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      const next = JSON.parse(JSON.stringify(settings));
      next.finance = next.finance || { fixed_expenses: [] };
      next.finance.fixed_expenses = next.finance.fixed_expenses || [];
      setDraft(next);
    }
  }, [settings]);

  const update = (path, val) => {
    setDraft((d) => {
      const next = JSON.parse(JSON.stringify(d));
      const keys = path.split(".");
      let o = next;
      for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]] = o[keys[i]] || {};
      o[keys[keys.length - 1]] = val;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", draft);
      setSettings(data);
      toast.success("Alterações salvas!");
      refresh();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  if (!draft) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Carregando…</div>;

  const mobileTabs = ["branding", "hero", "cards", "doctor", "services", "service_links", "units", "schedule", "finance", "testimonials", "faqs", "contact", "messages"];

  return (
    <div className="flex bg-background min-h-screen" data-testid="admin-dashboard">
      <Sidebar tab={tab} setTab={setTab} onLogout={handleLogout} />
      <div className="flex-1">
        <div className="sticky top-0 z-40 bg-white border-b border-border px-6 lg:px-10 py-4 flex justify-between items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Painel</div>
            <div className="font-display text-lg text-primary">Bem-vindo, {user?.name || user?.email}</div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/admin/usuarios" className="text-sm px-4 py-2 rounded-full border border-border hover:bg-secondary/40 inline-flex items-center gap-2" data-testid="view-admin-users">
              <Users className="w-4 h-4" /> Usuários
            </a>
            <a href="/painel" target="_blank" rel="noreferrer" className="text-sm px-4 py-2 rounded-full border border-border hover:bg-secondary/40 inline-flex items-center gap-2" data-testid="view-finance-panel">
              <Eye className="w-4 h-4" /> Painel financeiro
            </a>
            <a href="/" target="_blank" rel="noreferrer" className="text-sm px-4 py-2 rounded-full border border-border hover:bg-secondary/40 inline-flex items-center gap-2" data-testid="view-site">
              <Eye className="w-4 h-4" /> Ver site
            </a>
            {tab !== "messages" && (
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-accent text-accent-foreground font-medium hover:-translate-y-0.5 transition disabled:opacity-60" data-testid="admin-save">
                <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar"}
              </button>
            )}
            <div data-testid="admin-profile-menu">
              <UserProfileMenu />
            </div>
          </div>
        </div>

        <div className="p-6 lg:p-10 max-w-5xl">
          <div className="lg:hidden mb-6 overflow-x-auto flex gap-2">
            {mobileTabs.map((t) => (
              <button key={t} onClick={() => setTab(t)} className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs ${tab === t ? "bg-primary text-primary-foreground" : "bg-secondary/40 text-primary"}`}>{t}</button>
            ))}
          </div>

          {tab === "branding" && (
            <div className="space-y-6" data-testid="admin-branding">
              <h2 className="font-display text-2xl text-primary">Identidade Visual</h2>
              <div className="bg-white rounded-2xl p-6 border border-border space-y-5">
                <TextField label="Nome do site" value={draft.site.name} onChange={(v) => update("site.name", v)} testid="site-name" />
                <TextField label="Slogan / Tagline" value={draft.site.tagline} onChange={(v) => update("site.tagline", v)} testid="site-tagline" />
                <ImageUploader label="Logo" value={draft.site.logo} onChange={(v) => update("site.logo", v)} testid="upload-logo" />
                <ImageUploader label="Favicon (recomendado: 32x32 ou 64x64 .png)" value={draft.site.favicon} onChange={(v) => update("site.favicon", v)} testid="upload-favicon" />
              </div>
            </div>
          )}

          {tab === "hero" && (
            <div className="space-y-6" data-testid="admin-hero">
              <h2 className="font-display text-2xl text-primary">Carrossel da Home</h2>
              <ListEditor items={draft.hero_slides} onChange={(v) => update("hero_slides", v)} fields={[{ key: "kicker", label: "Linha pequena (cima)" }, { key: "title", label: "Título principal", type: "textarea", rows: 2 }, { key: "description", label: "Descrição", type: "textarea", rows: 3 }, { key: "image", label: "Imagem de fundo", type: "image" }, { key: "cta_text", label: "Texto do botão" }, { key: "cta_link", label: "Link do botão" }]} addLabel="Adicionar slide" emptyTemplate={{ kicker: "", title: "", description: "", image: "", cta_text: "Saiba Mais", cta_link: "/" }} testid="hero-list" />
            </div>
          )}

          {tab === "cards" && (
            <div className="space-y-6" data-testid="admin-cards">
              <h2 className="font-display text-2xl text-primary">Cards Rápidos (Home)</h2>
              <ListEditor items={draft.quick_cards} onChange={(v) => update("quick_cards", v)} fields={[{ key: "title", label: "Título" }, { key: "description", label: "Descrição", type: "textarea", rows: 2 }, { key: "image", label: "Ícone/Imagem", type: "image" }, { key: "link", label: "Link" }]} emptyTemplate={{ title: "", description: "", image: "", link: "/" }} testid="cards-list" />
            </div>
          )}

          {tab === "doctor" && (
            <div className="space-y-6" data-testid="admin-doctor">
              <h2 className="font-display text-2xl text-primary">Sobre o Médico</h2>
              <div className="bg-white rounded-2xl p-6 border border-border space-y-5">
                <TextField label="Nome" value={draft.doctor.name} onChange={(v) => update("doctor.name", v)} testid="doctor-name" />
                <TextField label="CRM / Registro" value={draft.doctor.crm} onChange={(v) => update("doctor.crm", v)} testid="doctor-crm" />
                <ImageUploader label="Foto principal" value={draft.doctor.photo} onChange={(v) => update("doctor.photo", v)} testid="upload-doctor-photo" />
                <ImageUploader label="Foto secundária" value={draft.doctor.secondary_photo} onChange={(v) => update("doctor.secondary_photo", v)} testid="upload-doctor-photo-2" />
                <TextField label="Bio curta" value={draft.doctor.bio_short} onChange={(v) => update("doctor.bio_short", v)} textarea rows={3} testid="doctor-bio-short" />
                <TextField label="Bio completa" value={draft.doctor.bio_full} onChange={(v) => update("doctor.bio_full", v)} textarea rows={8} testid="doctor-bio-full" />
                <TextField label="Citação / Quote" value={draft.doctor.quote} onChange={(v) => update("doctor.quote", v)} textarea rows={2} testid="doctor-quote" />
              </div>
            </div>
          )}

          {tab === "services" && (
            <div className="space-y-6" data-testid="admin-services">
              <h2 className="font-display text-2xl text-primary">Serviços</h2>
              <ListEditor items={draft.services} onChange={(v) => update("services", v)} fields={[{ key: "title", label: "Título" }, { key: "description", label: "Descrição", type: "textarea", rows: 2 }, { key: "image", label: "Imagem", type: "image" }, { key: "link", label: "Link" }]} emptyTemplate={{ title: "", description: "", image: "", link: "/contato" }} testid="services-list" />
            </div>
          )}

          {tab === "service_links" && (
            <div className="space-y-6" data-testid="admin-service-links">
              <h2 className="font-display text-2xl text-primary">Links de Serviços (submenu)</h2>
              <p className="text-sm text-muted-foreground -mt-3">Estes itens aparecem no submenu do "Serviços" no topo do site e na lista completa na página /servicos.</p>
              <ListEditor items={draft.service_links} onChange={(v) => update("service_links", v)} fields={[{ key: "title", label: "Nome do serviço" }, { key: "link", label: "URL (link externo ou interno)" }]} emptyTemplate={{ title: "", link: "" }} testid="service-links-list" />
            </div>
          )}

          {tab === "units" && (
            <div className="space-y-6" data-testid="admin-units">
              <h2 className="font-display text-2xl text-primary">Unidades de Atendimento</h2>
              <ListEditor items={draft.units} onChange={(v) => update("units", v)} fields={[{ key: "name", label: "Nome" }, { key: "address", label: "Endereço", type: "textarea", rows: 2 }, { key: "link", label: "Link" }]} emptyTemplate={{ name: "", address: "", link: "" }} testid="units-list" />
            </div>
          )}

          {tab === "schedule" && (
            <div className="space-y-6" data-testid="admin-schedule">
              <h2 className="font-display text-2xl text-primary">Horários de Atendimento</h2>
              <p className="text-sm text-muted-foreground -mt-3">Clique em Editar para alterar o dia ou o horário. Depois clique em Salvar no topo.</p>
              <ListEditor items={draft.schedule} onChange={(v) => update("schedule", v)} fields={[{ key: "day", label: "Dia" }, { key: "hours", label: "Horário" }]} addLabel="Adicionar horário" emptyTemplate={{ day: "", hours: "" }} testid="schedule-list" />
            </div>
          )}

          {tab === "finance" && (
            <FinanceEditor items={draft.finance?.fixed_expenses || []} onChange={(v) => update("finance.fixed_expenses", v)} />
          )}

          {tab === "testimonials" && (
            <div className="space-y-6" data-testid="admin-testimonials">
              <h2 className="font-display text-2xl text-primary">Depoimentos</h2>
              <ListEditor items={draft.testimonials} onChange={(v) => update("testimonials", v)} fields={[{ key: "name", label: "Nome do paciente" }, { key: "text", label: "Texto", type: "textarea", rows: 4 }, { key: "rating", label: "Avaliação (1-5)", type: "number" }]} emptyTemplate={{ name: "", text: "", rating: 5 }} testid="testimonials-list" />
            </div>
          )}

          {tab === "faqs" && (
            <div className="space-y-6" data-testid="admin-faqs">
              <h2 className="font-display text-2xl text-primary">Perguntas Frequentes</h2>
              <ListEditor items={draft.faqs} onChange={(v) => update("faqs", v)} fields={[{ key: "question", label: "Pergunta" }, { key: "answer", label: "Resposta", type: "textarea", rows: 3 }]} emptyTemplate={{ question: "", answer: "" }} testid="faqs-list" />
            </div>
          )}

          {tab === "contact" && (
            <div className="space-y-6" data-testid="admin-contact">
              <h2 className="font-display text-2xl text-primary">Contato & Redes Sociais</h2>
              <div className="bg-white rounded-2xl p-6 border border-border space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <TextField label="Telefone" value={draft.contact.phone} onChange={(v) => update("contact.phone", v)} testid="contact-phone-field" />
                  <TextField label="WhatsApp (somente números, com DDI)" value={draft.contact.whatsapp} onChange={(v) => update("contact.whatsapp", v)} testid="contact-whatsapp-field" />
                  <TextField label="E-mail" value={draft.contact.email} onChange={(v) => update("contact.email", v)} testid="contact-email-field" />
                  <TextField label="Endereço" value={draft.contact.address} onChange={(v) => update("contact.address", v)} testid="contact-address-field" />
                  <TextField label="Instagram URL" value={draft.contact.instagram} onChange={(v) => update("contact.instagram", v)} testid="contact-instagram-field" />
                  <TextField label="YouTube URL" value={draft.contact.youtube} onChange={(v) => update("contact.youtube", v)} testid="contact-youtube-field" />
                  <TextField label="Facebook URL" value={draft.contact.facebook} onChange={(v) => update("contact.facebook", v)} testid="contact-facebook-field" />
                  <TextField label="LinkedIn URL" value={draft.contact.linkedin} onChange={(v) => update("contact.linkedin", v)} testid="contact-linkedin-field" />
                </div>
              </div>
            </div>
          )}

          {tab === "messages" && <Messages />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
